import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const EmployeeRating = () => {
  const { employeeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [employee, setEmployee] = useState(location.state?.employee || null);
  const [kras, setKras] = useState([]);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [overallRating, setOverallRating] = useState(0);
  const [salaryData, setSalaryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);


  const ratingLabels = {
    5: 'Outstanding',
    4: 'Exceeds',
    3: 'Meets',
    2: 'Below',
    1: 'Unsatisfactory',
  };

  useEffect(() => {
    if (!employee && employeeId) {
      fetchEmployee();
    }
  }, [employeeId]);

  useEffect(() => {
    if (employee && employee.id) {
      loadKrasAndRatings();
      if (userRole === 'leadership') {
        fetchSalaryData();
      }
    }
  }, [employee, userRole]);

  const fetchEmployee = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/api/employees/${employeeId}`);
      setEmployee(data);
    } catch (error) {
      setError('Error fetching employee details');
      console.error('Error fetching employee:', error);
    }
    setLoading(false);
  };

  // Sequential load: KRAs first, then existing ratings, then fill defaults
  const loadKrasAndRatings = async () => {
    try {
      if (!employee.category) {
        setError('Employee category not found');
        return;
      }

      // Step 1: Fetch KRAs
      const kraData = await api.get(`/api/kras/${encodeURIComponent(employee.category)}`);
      const kraList = Array.isArray(kraData) ? kraData : kraData.kras || [];
      setKras(kraList);

      // Step 2: Fetch existing saved ratings
      let savedRatings = {};
      let savedComments = {};
      try {
        const ratingData = await api.get(`/api/ratings/${employeeId}`);
        if (ratingData && ratingData.kraRatings) {
          savedRatings = ratingData.kraRatings;
          // Comments could be a string (from Google Sheets) or an object
          if (typeof ratingData.comments === 'object' && ratingData.comments !== null) {
            savedComments = ratingData.comments;
          }
          console.log('Loaded saved ratings:', savedRatings);
        }
      } catch (e) {
        console.log('No existing ratings found, using defaults');
      }

      // Step 3: Merge — saved ratings take priority, fill gaps with default 3
      const mergedRatings = {};
      const mergedComments = {};
      kraList.forEach((kra) => {
        const kraId = kra.sNo || kra.id || kra.kra || kra.name;
        mergedRatings[kraId] = savedRatings[kraId] !== undefined ? savedRatings[kraId] : 3;
        mergedComments[kraId] = savedComments[kraId] || '';
      });

      setRatings(mergedRatings);
      setComments(mergedComments);
    } catch (error) {
      setError('Error fetching KRAs');
      console.error('Error loading KRAs and ratings:', error);
    }
  };

  const fetchSalaryData = async () => {
    try {
      const data = await api.get(`/api/salary/${employeeId}`);
      setSalaryData(data || {});
    } catch (error) {
      console.error('Error fetching salary data:', error);
    }
  };

  const getKraId = (kra) => kra.sNo || kra.id || kra.kra || kra.name;
  const getKraName = (kra) => kra.kra || kra.name || `KRA ${kra.sNo}`;

  // initializeRatings is now handled inside loadKrasAndRatings

  const calculateWeightedAverage = () => {
    if (kras.length === 0) return 0;

    let totalWeighted = 0;
    let totalWeight = 0;

    kras.forEach((kra) => {
      const kraId = getKraId(kra);
      const rating = parseFloat(ratings[kraId]) || 3;
      const weightage = parseFloat(kra.weightage) || 0;
      totalWeighted += rating * weightage;
      totalWeight += weightage;
    });

    const avg = totalWeight > 0 ? totalWeighted / totalWeight : 0;
    setOverallRating(avg.toFixed(2));
    return avg.toFixed(2);
  };

  useEffect(() => {
    if (Object.keys(ratings).length > 0) {
      calculateWeightedAverage();
    }
  }, [ratings]);

  const handleSaveRating = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        kraRatings: ratings,
        comments,
        overallRating: parseFloat(overallRating) || 0,
      };

      const result = await api.post(`/api/ratings/${employeeId}`, payload);
      console.log('Rating saved:', result);

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        navigate('/employees');
      }, 2000);
    } catch (error) {
      setError('Error saving rating. Please try again.');
      console.error('Error saving rating:', error);
    }
    setLoading(false);
  };

  if (loading && !employee) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading employee details...</div>;
  }

  if (!employee) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#d32f2f' }}>
        {error || 'Employee not found'}
      </div>
    );
  }

  if (kras.length === 0 && !loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#d32f2f' }}>
        {error || 'Unable to load KRAs for this employee'}
      </div>
    );
  }

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    header: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      borderLeft: '6px solid #e94560',
    },
    headerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginTop: '16px',
    },
    headerItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#999',
      textTransform: 'uppercase',
      marginBottom: '6px',
    },
    headerValue: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1a1a2e',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1a1a2e',
      marginBottom: '20px',
    },
    kraContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    kraItem: {
      paddingBottom: '20px',
      borderBottom: '1px solid #f0f0f0',
    },
    kraItemLast: {
      borderBottom: 'none',
      paddingBottom: 0,
    },
    kraHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    kraName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1a1a2e',
    },
    weightage: {
      fontSize: '12px',
      color: '#999',
    },
    ratingControls: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      marginBottom: '12px',
    },
    ratingButton: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e0e0e0',
      backgroundColor: 'white',
      color: '#666',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    ratingButtonActive: {
      backgroundColor: '#e94560',
      color: 'white',
      borderColor: '#e94560',
    },
    ratingLabel: {
      fontSize: '12px',
      color: '#999',
      fontStyle: 'italic',
      marginLeft: '8px',
    },
    commentBox: {
      width: '100%',
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #e0e0e0',
      fontSize: '13px',
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '80px',
      transition: 'all 0.3s ease',
    },
    overallRatingCard: {
      backgroundColor: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      borderRadius: '12px',
      padding: '24px',
      color: 'white',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    overallTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#aaa',
      marginBottom: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    overallValue: {
      fontSize: '48px',
      fontWeight: '700',
      marginBottom: '8px',
    },
    overallSubtext: {
      fontSize: '14px',
      color: '#ccc',
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px',
    },
    button: {
      flex: 1,
      padding: '12px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    primaryButton: {
      backgroundColor: '#e94560',
      color: 'white',
    },
    secondaryButton: {
      backgroundColor: '#f0f0f0',
      color: '#1a1a2e',
    },
    successMessage: {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
      padding: '12px 16px',
      borderRadius: '6px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    salarySection: {
      backgroundColor: '#f9f9f9',
      padding: '16px',
      borderRadius: '8px',
      marginTop: '16px',
    },
  };

  return (
    <div style={styles.container}>
      {error && (
        <div style={{ ...styles.successMessage, backgroundColor: '#ffebee', color: '#c62828' }}>
          <span>!</span>
          <span>{error}</span>
        </div>
      )}

      {saveSuccess && (
        <div style={styles.successMessage}>
          <span>✓</span>
          <span>Rating saved successfully!</span>
        </div>
      )}

      <div style={styles.header}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#999', textTransform: 'uppercase' }}>
          Employee Details
        </div>
        <div style={styles.headerGrid}>
          <div style={styles.headerItem}>
            <div style={styles.headerLabel}>Name</div>
            <div style={styles.headerValue}>{employee.name}</div>
          </div>
          <div style={styles.headerItem}>
            <div style={styles.headerLabel}>Designation</div>
            <div style={styles.headerValue}>{employee.designation}</div>
          </div>
          <div style={styles.headerItem}>
            <div style={styles.headerLabel}>Category</div>
            <div style={styles.headerValue}>{employee.category}</div>
          </div>
          <div style={styles.headerItem}>
            <div style={styles.headerLabel}>Reporting Head</div>
            <div style={styles.headerValue}>{employee.reportingHead}</div>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Key Result Areas (KRAs)</div>
        <div style={styles.kraContainer}>
          {kras.map((kra, idx) => {
            const kraId = getKraId(kra);
            const kraName = getKraName(kra);
            return (
              <div
                key={kraId}
                style={{
                  ...styles.kraItem,
                  ...(idx === kras.length - 1 && styles.kraItemLast),
                }}
              >
                <div style={styles.kraHeader}>
                  <div>
                    <div style={styles.kraName}>{kra.sNo}. {kraName}</div>
                    {kra.successMeasures && (
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                        {kra.successMeasures}
                      </div>
                    )}
                    {(kra.unit || kra.target) && (
                      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                        {kra.unit && <span>Unit: {kra.unit}</span>}
                        {kra.unit && kra.target && <span> | </span>}
                        {kra.target && <span>Target: {kra.target}</span>}
                      </div>
                    )}
                  </div>
                  <div style={styles.weightage}>Weightage: {kra.weightage}%</div>
                </div>

                <div style={styles.ratingControls}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      style={{
                        ...styles.ratingButton,
                        ...(ratings[kraId] === rating &&
                          styles.ratingButtonActive),
                      }}
                      onClick={() =>
                        setRatings({ ...ratings, [kraId]: rating })
                      }
                    >
                      {rating}
                    </button>
                  ))}
                  <div style={styles.ratingLabel}>
                    {ratingLabels[ratings[kraId]] || ''}
                  </div>
                </div>

                <textarea
                  style={styles.commentBox}
                  placeholder={`Add comments for ${kraName}...`}
                  value={comments[kraId] || ''}
                  onChange={(e) =>
                    setComments({ ...comments, [kraId]: e.target.value })
                  }
                  onFocus={(e) => {
                    e.target.style.borderColor = '#e94560';
                    e.target.style.boxShadow =
                      '0 0 0 3px rgba(233, 69, 96, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ ...styles.overallRatingCard, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div style={styles.overallTitle}>Overall Weighted Rating</div>
        <div style={styles.overallValue}>{overallRating}</div>
        <div style={styles.overallSubtext}>Out of 5.0</div>
      </div>

      {userRole === 'leadership' && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Salary & Increment Details</div>
          <div style={styles.salarySection}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={styles.headerLabel}>Current Salary</div>
                <div style={styles.headerValue}>
                  ₹ {(salaryData.currentMonthlyCTC || salaryData.currentSalary || 0).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={styles.headerLabel}>Proposed Increment</div>
                <div style={styles.headerValue} style={{ color: '#8bc34a' }}>
                  ₹ {(salaryData.proposedIncrement || 0).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div style={styles.headerLabel}>New Salary</div>
                <div style={styles.headerValue}>
                  ₹ {((salaryData.currentMonthlyCTC || salaryData.currentSalary || 0) + (salaryData.proposedIncrement || 0)).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '12px' }}>
              Leadership users can override ratings and adjust salary increments as needed.
            </div>
          </div>
        </div>
      )}

      <div style={styles.buttonContainer}>
        <button
          style={{
            ...styles.button,
            ...styles.secondaryButton,
          }}
          onClick={() => navigate('/employees')}
        >
          Cancel
        </button>
        <button
          style={{
            ...styles.button,
            ...styles.primaryButton,
          }}
          onClick={handleSaveRating}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) e.target.style.backgroundColor = '#d63a52';
          }}
          onMouseLeave={(e) => {
            if (!loading) e.target.style.backgroundColor = '#e94560';
          }}
        >
          {loading ? 'Saving...' : 'Save Rating'}
        </button>
      </div>
    </div>
  );
};

export default EmployeeRating;
